/**
 * BlueprintsHeader — shared header component for Blueprints sub-pages.
 *
 * Shared across all five Blueprints pages (Templates, Task Runs, Actions,
 * Custom Fields, Authoring Guide). Each page imports this directly.
 *
 * ## What renders here
 *
 * - Backstage <Header> with "Blueprints" title, a page-specific subtitle,
 *   and a browser-tab pageTitleOverride.
 * - <HeaderTabs> with all five Blueprints tabs. Clicking a tab navigates
 *   imperatively; the active index is derived from the current pathname.
 * - "Edit a template" secondary action CTA (Phase 4.1 step 1) — a
 *   <LinkButton> linking to /create/edit. Rendered as a child of <Header>
 *   so it appears in the header's top-right action area, above the tab strip.
 *
 * ## Tab routing
 *
 *   Tab 0 – Templates    → /create
 *   Tab 1 – Task Runs    → /create/tasks   (our custom TasksListPage)
 *   Tab 2 – Actions      → /create/actions (our custom ActionsCatalogPage)
 *   Tab 3 – Custom Fields → /create/custom-fields
 *   Tab 4 – Authoring Guide → /create/guide
 *
 * Active tab determination (pathname prefix matching):
 *   /create/guide*         → 4
 *   /create/custom-fields* → 3
 *   /create/actions*       → 2
 *   /create/tasks*         → 1
 *   everything else        → 0  (includes /create and /create/edit)
 *
 * All five tabs are always visible for orientation regardless of which page
 * the user is on. Tabs 0 (Templates) and the /edit CTA navigate into
 * ScaffolderPage-owned chrome; tabs 1–4 navigate to pages that each render
 * this component as their header.
 *
 * ## "Edit a template" CTA
 *
 * Links to /create/edit, which is handled by ScaffolderPage's internal router
 * (TemplateEditorPage). Our three custom field extensions are registered there
 * because they are passed as props.children to <ScaffolderPage> inside
 * BlueprintsTemplatesTab.tsx (the direct FlatRoutes mount at /create).
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from '@backstage/ui';
import { configApiRef, useApi } from '@backstage/core-plugin-api';

// ── Tab definitions ──────────────────────────────────────────────────────────

/**
 * Canonical ordered list of Blueprints tabs.
 * Indices are stable — always reference tabs by index (0–4), not by id.
 */
export const BLUEPRINTS_TABS = [
  { id: 'templates', label: 'Templates', path: '/create' },
  { id: 'tasks', label: 'Task Runs', path: '/create/tasks' },
  { id: 'actions', label: 'Actions', path: '/create/actions' },
  {
    id: 'custom-fields',
    label: 'Custom Fields',
    path: '/create/custom-fields',
  },
  { id: 'guide', label: 'Authoring Guide', path: '/create/guide' },
] as const;

/** Tab links for BUI's <Header>, hoisted so the array is built once. */
const HEADER_TABS = BLUEPRINTS_TABS.map(t => ({
  id: t.id,
  label: t.label,
  href: t.path,
}));

// ── Active tab hook ──────────────────────────────────────────────────────────

/**
 * Returns true when `pathname` exactly equals `tabPath` or begins with
 * `tabPath + "/"`. This prevents `/create/tasks` from spuriously matching
 * a future similarly-prefixed path like `/create/tasksmith`.
 */
function pathMatchesTab(pathname: string, tabPath: string): boolean {
  return pathname === tabPath || pathname.startsWith(`${tabPath}/`);
}

/**
 * Returns the 0-based index of the active Blueprints tab for the current
 * pathname. Uses precise prefix matching so sub-routes (e.g.
 * /create/tasks/abc123) keep the parent tab highlighted without false
 * positives on similarly-prefixed paths.
 */
export function useActiveBlueprintsTabIndex(): number {
  const { pathname } = useLocation();
  if (pathMatchesTab(pathname, '/create/guide')) return 4;
  if (pathMatchesTab(pathname, '/create/custom-fields')) return 3;
  if (pathMatchesTab(pathname, '/create/actions')) return 2;
  if (pathMatchesTab(pathname, '/create/tasks')) return 1;
  // Default: Templates tab, covers /create and /create/edit
  return 0;
}

// ── Shared header component ──────────────────────────────────────────────────

/**
 * Sets the browser-tab title while the page is shown, as core-components'
 * <Header pageTitleOverride> did; BUI's <Header> does not touch the title.
 */
function usePageTitle(pageTitle: string) {
  const appTitle =
    useApi(configApiRef).getOptionalString('app.title') ?? 'Backstage';
  useEffect(() => {
    const previous = document.title;
    document.title = `${pageTitle} | ${appTitle}`;
    return () => {
      document.title = previous;
    };
  }, [pageTitle, appTitle]);
}

/**
 * Blueprints chrome: BUI's <Header> with the Blueprints tabs as links, so
 * every Blueprints page shares the same header and tab strip.
 *
 * The "Edit a template" CTA lives in the Templates tab toolbar, where it is
 * most relevant and doesn't compete with page chrome.
 *
 * @param subtitle          - Short description shown beneath "Blueprints".
 * @param pageTitleOverride - String used as the browser-tab / document title.
 */
export function BlueprintsHeader({
  subtitle,
  pageTitleOverride,
}: {
  subtitle: string;
  pageTitleOverride: string;
}): JSX.Element {
  const activeIndex = useActiveBlueprintsTabIndex();
  usePageTitle(pageTitleOverride);

  return (
    <Header
      title="Blueprints"
      description={subtitle}
      tabs={HEADER_TABS}
      activeTabId={BLUEPRINTS_TABS[activeIndex].id}
    />
  );
}
